/**
 * ACH Helper Functions
 * These functions provide centralized logic for ACH-related display decisions
 * and can be accessed from anywhere in the application.
 */

/**
 * Check if Dwolla is properly configured for the current client
 * @returns {boolean} True if Dwolla is configured with valid environment
 */
const isDwollaConfigured = () => {
	const dwollaEnv = globalThis.clientData?.ach_env;
	return dwollaEnv === 'dwolla_sandbox' || dwollaEnv === 'dwolla_prod';
};

/**
 * Check if ACH tab should be shown based on client configuration
 * @returns {boolean} True if ACH tab should be displayed
 */
const showAchTab = () => {
	return !!(globalThis.clientData?.tabs?.show_ach && isDwollaConfigured());
};

const getDwollaAchKey = () => {
	const env = globalThis.clientData?.ach_env;
	return env === 'dwolla_prod' ? 'ach_record_dwolla_prod' : 'ach_record_dwolla_sandbox';
};

const getUserDwollaData = (user) => user[getDwollaAchKey()];

// #6686: a payee whose bank account (Dwolla funding source) changed after the cutoff must be reviewed before a
// distribution pays them. Same rule as NewServerless payment-processor-proxy-worker and NthWorkflows ach_dist.
const bankChangedAt = (ach) =>
	['primary', 'secondary']
		.map((k) => (ach || {})[`${k}_funding_source_id_ts`] || '')
		.sort()
		.pop();

// ISO cutoff: the share class's previous event before this one (events: [{nrid, date (unix)}]), else 90 days back
const bankReviewCutoff = (events, nrid) => {
	const when = (e) => Number(e?.date);
	const thisDate = when((events || []).find((e) => String(e.nrid) === String(nrid))) || Date.now() / 1000;
	const earlier = (events || []).map(when).filter((d) => d < thisDate);
	const since = earlier.length ? Math.max(...earlier) : Date.now() / 1000 - 90 * 86400;
	return new Date(since * 1000).toISOString();
};

// Export functions to global scope for backward compatibility
if (typeof window !== 'undefined') {
	window.isDwollaConfigured = isDwollaConfigured;
	window.showAchTab = showAchTab;
	window.getDwollaAchKey = getDwollaAchKey;
	window.getUserDwollaData = getUserDwollaData;
	window.bankChangedAt = bankChangedAt;
	window.bankReviewCutoff = bankReviewCutoff;
}

// Export for module systems (if applicable)
if (typeof module !== 'undefined' && module.exports) {
	module.exports = {
		isDwollaConfigured,
		showAchTab,
		getDwollaAchKey,
		getUserDwollaData,
		bankChangedAt,
		bankReviewCutoff,
	};
}
